import React from 'react';

import { useTheme } from '@/contexts/ThemeContext';
import { useBookCoverVideo } from '@/hooks/useBookCoverVideo';
import { Book } from '@/utils/bookRouting';

interface BrowseBookCardProps {
  book: Book;
  onBookClick: (book: Book) => void;
}

const formatDuration = (totalDuration: number): string => {
  const totalMinutes = Math.round(totalDuration / 60);

  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

const BrowseBookCard: React.FC<BrowseBookCardProps> = ({ book, onBookClick }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const {
    videoRef,
    showVideo,
    handleMouseEnter,
    handleMouseLeave,
    coverVideoPath,
  } = useBookCoverVideo(book.coverAnimationUrl);

  return (
    <button
      type="button"
      className={`group flex w-full cursor-pointer flex-col items-start text-left ${
        isDarkMode ? 'text-theme-primary-dark' : 'text-theme-primary-light'
      }`}
      onClick={() => onBookClick(book)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="relative aspect-[2/3] w-full overflow-hidden rounded-sm shadow-[0_14px_35px_rgba(0,0,0,0.18)] transition-transform duration-200 group-hover:-translate-y-1">
        <img
          src={book.coverUrl || ''}
          alt={`${book.title} by ${book.author} - Book Cover`}
          className="absolute inset-0 block h-full w-full object-cover transition-opacity duration-500"
        />
        {coverVideoPath ? (
          <video
            ref={videoRef}
            src={coverVideoPath}
            className="absolute inset-0 block h-full w-full object-cover transition-opacity duration-500"
            style={{
              opacity: showVideo ? 1 : 0,
              pointerEvents: showVideo ? 'auto' : 'none',
            }}
            preload="metadata"
            loop
            muted
            playsInline
            aria-label={`${book.title} by ${book.author} - Animated Book Cover`}
          >
            Your browser does not support the video tag.
          </video>
        ) : null}
      </div>
      <div className="mt-4 flex w-full items-center justify-between gap-4 text-xs uppercase tracking-[0.1em] opacity-65">
        <span>{formatDuration(book.totalDuration)}</span>
      </div>
    </button>
  );
};

export default BrowseBookCard;
